import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { path_url } from "../../Config/config";
import {
  FaUserPlus,
  FaExchangeAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaCalendarAlt,
  FaStickyNote,
  FaHistory,
  FaSearch,
  FaUserCheck,
  FaShareAlt,
} from "react-icons/fa";

// Format activity types to clean human readable labels
const formatActivityType = (rawType = "") => {
  const upper = rawType.toUpperCase();
  if (upper.includes("CALL")) return "Phone Call";
  if (upper.includes("EMAIL")) return "Email Sent";
  if (upper.includes("MEETING") || upper.includes("DEMO")) return "Meeting Scheduled";
  if (upper.includes("FOLLOW")) return "Follow-up Scheduled";
  if (upper.includes("NOTE")) return "Note Added";
  if (["FACEBOOK", "INSTAGRAM", "LINKEDIN", "PINTEREST", "TELEGRAM", "WHATSAPP", "SOCIAL"].some((s) => upper.includes(s))) {
    return "Social Activity";
  }
  return rawType || "Lead Activity";
};

// Select icon by category/type
const getIconForEvent = (category, typeStr = "") => {
  if (category === "stage_history") {
    if (typeStr === "Lead Created") return <FaUserPlus className="text-primary" size={14} />;
    return <FaExchangeAlt className="text-secondary" size={14} />;
  }

  const upper = typeStr.toUpperCase();
  if (upper.includes("CALL")) return <FaPhoneAlt className="text-info" size={14} />;
  if (upper.includes("EMAIL")) return <FaEnvelope className="text-warning" size={14} />;
  if (upper.includes("MEETING") || upper.includes("DEMO")) return <FaCalendarAlt className="text-success" size={14} />;
  if (upper.includes("FOLLOW")) return <FaCalendarAlt className="text-primary" size={14} />;
  if (upper.includes("NOTE")) return <FaStickyNote className="text-warning" size={14} />;
  if (["FACEBOOK", "INSTAGRAM", "LINKEDIN", "PINTEREST", "TELEGRAM", "WHATSAPP", "SOCIAL"].some((s) => upper.includes(s))) {
    return <FaShareAlt className="text-info" size={14} />;
  }
  return <FaHistory className="text-secondary" size={14} />;
};

// Select badge style
const getBadgeStyle = (category, typeStr = "") => {
  if (category === "stage_history") {
    return "bg-blue-50 text-blue-700 border-blue-200";
  }
  const upper = typeStr.toUpperCase();
  if (upper.includes("CALL")) return "bg-sky-50 text-sky-700 border-sky-200";
  if (upper.includes("EMAIL")) return "bg-amber-50 text-amber-700 border-amber-200";
  if (upper.includes("MEETING")) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (upper.includes("NOTE")) return "bg-slate-100 text-slate-700 border-slate-200";
  return "bg-purple-50 text-purple-700 border-purple-200";
};

// FIX: heading was hardcoded to "Lead Activity & History Timeline" on every
// page that renders this component (Customer, Vendor, Lead). Capitalizing
// the passed-in `type` prop lets each page show its own correct label
// ("Customer Activity & History Timeline", "Vendor Activity & History
// Timeline", etc.) without duplicating this whole component per entity.
const capitalize = (str = "") => (str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "");

// Memoized: only re-renders when leadId changes
// Prevents Formik / parent state re-renders from triggering extra API calls
const Status = React.memo(function StatusTimeline({ leadId, type = "lead" }) {
  const entityLabel = capitalize(type) || "Lead";

  // Local state for fetched API data (Loaded ONCE per leadId)
  const [activitiesState, setActivitiesState] = useState([]);
  const [stageHistoryState, setStageHistoryState] = useState([]);
  const [loading, setLoading] = useState(false);

  // Local UI filters (NO API CALLS on change)
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  // Fetch Stage History & Lead Activity ONCE when leadId changes
  useEffect(() => {
    if (!leadId) return;

    let isMounted = true;
    const fetchTimelineData = async () => {
      setLoading(true);
      const token = localStorage.getItem("accessToken");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      try {
        const [activitiesRes, stageHistoryRes] = await Promise.allSettled([
          axios.get(`${path_url}/lead-activity/lead/${leadId}`, { headers }),
          axios.get(`${path_url}/stage-history/lead/${leadId}`, { headers }),
        ]);

        if (isMounted) {
          if (activitiesRes.status === "fulfilled" && activitiesRes.value?.data) {
            const rawActs = activitiesRes.value.data.body || activitiesRes.value.data || [];
            setActivitiesState(Array.isArray(rawActs) ? rawActs : []);
          } else {
            setActivitiesState([]);
          }

          if (stageHistoryRes.status === "fulfilled" && stageHistoryRes.value?.data) {
            const rawStages = stageHistoryRes.value.data.body || stageHistoryRes.value.data || [];
            setStageHistoryState(Array.isArray(rawStages) ? rawStages : []);
          } else {
            setStageHistoryState([]);
          }
        }
      } catch (err) {
        console.error("Timeline audit fetch error:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchTimelineData();

    return () => {
      isMounted = false;
    };
  }, [leadId]);

  // Merge Stage History & Lead Activity APIs into a single Lead Journey stream
  const timelineEvents = useMemo(() => {
    const events = [];

    // 1. Process Stage History Records
    if (Array.isArray(stageHistoryState)) {
      stageHistoryState.forEach((sh, idx) => {
        const isCreation = !sh.fromStatus || sh.fromStatus === "Initial";
        events.push({
          id: `stage-${sh.id || idx}`,
          category: "stage_history",
          rawType: isCreation ? `${entityLabel} Created` : "Status Changed",
          title: isCreation ? `${entityLabel} Created` : "Status Changed",
          description: sh.changeReason
            ? `Reason: ${sh.changeReason}`
            : isCreation
            ? `New ${entityLabel.toLowerCase()} created in system`
            : `Status updated from ${sh.fromStatus || "Initial"} to ${sh.toStatus || "New Status"}`,
          user: sh.changedBy || sh.createdBy || sh.user || "System",
          timestamp: sh.changedAt || sh.createdDate || sh.date || "Recent",
          fromStatus: sh.fromStatus || "",
          toStatus: sh.toStatus || "",
          changeReason: sh.changeReason || "",
          subject: "",
          notes: sh.changeReason || "",
          outcome: "",
        });
      });
    }

    // 2. Process Lead Activity Records
    if (Array.isArray(activitiesState)) {
      activitiesState.forEach((act, idx) => {
        const formattedType = formatActivityType(act.type);
        events.push({
          id: `act-${act.id || idx}`,
          category: "activity",
          rawType: act.type || "Activity",
          title: formattedType,
          description: act.notes || act.outcome ? `Notes: ${act.notes || "-"} ${act.outcome ? `| Outcome: ${act.outcome}` : ""}` : `Activity recorded`,
          user: act.user || act.createdBy || act.agent || "Agent",
          timestamp: act.activityDate || act.createdDate || act.date || "Recent",
          fromStatus: "",
          toStatus: "",
          changeReason: "",
          subject: act.subject || "",
          notes: act.notes || "",
          outcome: act.outcome || "",
        });
      });
    }

    // Sort chronologically (latest timestamp first)
    return events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [stageHistoryState, activitiesState, entityLabel]);

  // Local Tab Filtering & Text Search (100% In-Memory, ZERO Network Calls)
  const filteredEvents = useMemo(() => {
    return timelineEvents.filter((item) => {
      // Tab Filter
      let matchesTab = true;
      const rawUpper = (item.rawType || "").toUpperCase();

      if (activeTab === "stage") {
        matchesTab = item.category === "stage_history";
      } else if (activeTab === "calls") {
        matchesTab = rawUpper.includes("CALL");
      } else if (activeTab === "emails") {
        matchesTab = rawUpper.includes("EMAIL");
      } else if (activeTab === "meetings") {
        matchesTab = rawUpper.includes("MEETING") || rawUpper.includes("DEMO");
      } else if (activeTab === "notes") {
        matchesTab = rawUpper.includes("NOTE") || rawUpper.includes("FOLLOW");
      }

      if (!matchesTab) return false;

      // Text Search Filter
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();

      return (
        item.title.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        (item.subject && item.subject.toLowerCase().includes(q)) ||
        (item.notes && item.notes.toLowerCase().includes(q)) ||
        (item.outcome && item.outcome.toLowerCase().includes(q)) ||
        (item.changeReason && item.changeReason.toLowerCase().includes(q)) ||
        (item.fromStatus && item.fromStatus.toLowerCase().includes(q)) ||
        (item.toStatus && item.toStatus.toLowerCase().includes(q)) ||
        (item.user && item.user.toLowerCase().includes(q))
      );
    });
  }, [timelineEvents, activeTab, searchQuery]);

  return (
    <div className="card border-0 shadow-sm rounded-3 overflow-hidden bg-white mb-4">
      {/* Header & Local Controls */}
      {/* FIX: was `flex-wrap` at both the outer row AND the inner
          tabs+search group, so once the title + 6 tab pills + search box
          couldn't all fit on one line, the SEARCH BOX specifically was
          the item most likely to drop to its own row below the tabs
          (it's last in DOM order in this flex group). We now keep the
          title in its own row on small screens, and keep tabs + search
          together with `flex-nowrap` + horizontal scroll instead of
          wrapping, so search always stays inline, to the right of the
          tabs, as requested. */}
      <div className="card-header bg-white py-3 border-bottom">
        {/* Title row */}
        <div className="d-flex align-items-center mb-2">
          <h5 className="card-title mb-0 fw-bold text-dark fs-6 d-flex align-items-center gap-2">
            <span className="rounded-2 p-1.5 px-2 bg-slate-100 text-secondary">
              <FaHistory size={15} />
            </span>
            {entityLabel} Activity & History Timeline
          </h5>
        </div>

        {/* Tabs + Search row — always on one line with horizontal scroll */}
        <div
          className="d-flex align-items-center gap-2"
          style={{
            flexWrap: "nowrap",
            overflowX: "auto",
            maxWidth: "100%",
            paddingBottom: "2px",
          }}
        >
          {/* Tab Filter Pills */}
          <div
            className="btn-group btn-group-sm rounded-pill p-0.5 bg-slate-100 border border-slate-200"
            style={{ flexShrink: 0 }}
          >
            {[
              { id: "all", label: "All" },
              { id: "stage", label: "Stage History" },
              { id: "calls", label: "Calls" },
              { id: "emails", label: "Emails" },
              { id: "meetings", label: "Meetings" },
              { id: "notes", label: "Notes" },
            ].map((tab) => (
              <button
                key={tab.id}
                className={`btn btn-xs rounded-pill px-2.5 py-1 ${
                  activeTab === tab.id
                    ? "bg-white text-dark shadow-xs fw-semibold"
                    : "text-muted border-0 bg-transparent"
                }`}
                style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Spacer pushes search to the right */}
          <div style={{ flex: 1, minWidth: "8px" }} />

          {/* Search — "Search: [🔍 input]" inline, matches reference design */}
          <div
            className="d-flex align-items-center gap-2"
            style={{ flexShrink: 0 }}
          >
            <span
              className="text-muted fw-semibold"
              style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
            >
              Search:
            </span>
            <div
              className="d-flex align-items-center bg-white border rounded"
              style={{
                width: "210px",
                height: "32px",
                padding: "0 10px",
                gap: "6px",
                borderColor: "#d1d5db",
                borderRadius: "6px",
              }}
            >
              <FaSearch size={11} color="#9ca3af" style={{ flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search activities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  border: "none",
                  outline: "none",
                  fontSize: "0.8rem",
                  width: "100%",
                  background: "transparent",
                  color: "#374151",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Stream */}
      <div className="card-body p-4">
        {loading ? (
          <div className="text-center py-4 text-muted small">Loading {entityLabel.toLowerCase()} history & activities...</div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-5">
            <FaHistory className="text-slate-300 mb-2" size={32} />
            <p className="text-muted small mb-0">No {entityLabel.toLowerCase()} history or activity records found.</p>
          </div>
        ) : (
          <div className="position-relative ps-3 ps-md-4" style={{ borderLeft: "2px solid #e2e8f0", marginLeft: "12px" }}>
            {filteredEvents.map((item) => (
              <div key={item.id} className="position-relative pb-4 ms-3 ms-md-4">
                {/* Node Circle */}
                <div
                  className="position-absolute d-flex align-items-center justify-content-center bg-white shadow-xs rounded-circle border"
                  style={{
                    left: "-38px",
                    top: "0px",
                    width: "32px",
                    height: "32px",
                    borderColor: "#cbd5e1",
                  }}
                >
                  {getIconForEvent(item.category, item.rawType)}
                </div>

                {/* Timeline Event Card */}
                <div className="p-3 rounded-3 border bg-white shadow-xs hover-border-slate transition-all" style={{ borderColor: "#e2e8f0" }}>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-semibold text-dark fs-6" style={{ fontSize: "0.9rem" }}>
                        {item.title}
                      </span>
                      <span className={`badge border px-2 py-0.5 rounded-pill text-xs fw-medium ${getBadgeStyle(item.category, item.rawType)}`} style={{ fontSize: "0.7rem" }}>
                        {item.category === "stage_history" ? "Stage Transition" : "Activity"}
                      </span>
                    </div>

                    <span className="text-muted small" style={{ fontSize: "0.75rem" }}>
                      {item.timestamp}
                    </span>
                  </div>

                  {item.subject && (
                    <div className="fw-medium text-dark small mb-1" style={{ fontSize: "0.84rem" }}>
                      Subject: {item.subject}
                    </div>
                  )}

                  <p className="text-secondary small mb-2" style={{ fontSize: "0.84rem", lineHeight: "1.4" }}>
                    {item.description}
                  </p>

                  <div className="d-flex align-items-center justify-content-between border-top pt-2 mt-2" style={{ borderColor: "#f1f5f9" }}>
                    <div className="d-flex align-items-center gap-1.5 text-muted small" style={{ fontSize: "0.75rem" }}>
                      <FaUserCheck size={12} className="text-secondary" />
                      <span>{item.user}</span>
                    </div>

                    {item.fromStatus && item.toStatus && (
                      <div className="d-flex align-items-center gap-1.5 text-muted small" style={{ fontSize: "0.75rem" }}>
                        <span className="badge bg-slate-100 text-slate-600 border fw-normal">{item.fromStatus}</span>
                        <span>&rarr;</span>
                        <span className="badge bg-slate-800 text-white fw-medium">{item.toStatus}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

export default Status;